/**
 * Wallet Service
 *
 * Provides React hooks and callable wrappers for the wallet feature.
 * All data is sourced from live Firestore.
 */

import { useState, useEffect } from 'react';
import {
    collection, query, where, orderBy, limit,
    onSnapshot, doc, type Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface WalletData {
    balance: number;
    lockedBalance: number;
    totalDeposited: number;
    totalWithdrawn: number;
    promoBalance: number;
    penaltyDebt: number;
    walletStatus: 'unfrozen' | 'frozen';
}

export interface PromoBalance {
    id: string;
    codeId: string;
    amount: number;
    originalAmount: number;
    validForGame: 'all' | 'pubg' | 'freefire' | 'mlbb' | 'wildrift';
    expiresAt: Date;
    redeemedAt: Date;
}

export interface LiveTransaction {
    id: string;
    type: 'deposit' | 'withdrawal' | 'entry' | 'reward';
    amount: number;
    description: string;
    status: 'pending' | 'approved' | 'rejected';
    method?: string;
    createdAt: string;
}

// ─── Hook: useWalletData ────────────────────────────────────────────────────

export function useWalletData(uid: string | undefined) {
    const [wallet, setWallet] = useState<WalletData>({
        balance: 0,
        lockedBalance: 0,
        totalDeposited: 0,
        totalWithdrawn: 0,
        promoBalance: 0,
        penaltyDebt: 0,
        walletStatus: 'unfrozen',
    });
    const [transactions, setTransactions] = useState<LiveTransaction[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!uid) { setLoading(false); return; }

        // ── Listen to wallet balance ──
        const walletRef = doc(db, `users/${uid}/private/wallet`);
        const unsubWallet = onSnapshot(walletRef, (snap) => {
            if (snap.exists()) {
                const d = snap.data();
                setWallet({
                    balance: d.balance ?? 0,
                    lockedBalance: d.lockedBalance ?? 0,
                    totalDeposited: d.totalDeposited ?? 0,
                    totalWithdrawn: d.totalWithdrawn ?? 0,
                    promoBalance: d.promoBalance ?? 0,
                    penaltyDebt: d.penaltyDebt ?? 0,
                    walletStatus: d.walletStatus ?? 'unfrozen',
                });
            }
            setLoading(false);
        }, (err) => {
            console.error('Wallet listener error:', err);
            setLoading(false);
        });

        // ── Listen to transaction history ──
        const txQuery = query(
            collection(db, 'transactions'),
            where('userId', '==', uid),
            orderBy('createdAt', 'desc'),
            limit(20),
        );
        const unsubTx = onSnapshot(txQuery, (snap) => {
            const txns: LiveTransaction[] = snap.docs.map((d) => {
                const data = d.data();
                // createdAt may be a Firestore Timestamp or null (pending write)
                let createdAtStr = '';
                if (data.createdAt) {
                    const ts = data.createdAt as Timestamp;
                    createdAtStr = ts.toDate
                        ? ts.toDate().toLocaleString()
                        : String(data.createdAt);
                }
                return {
                    id: d.id,
                    type: data.type,
                    amount: data.amount,
                    description: data.description ?? '',
                    status: data.status,
                    method: data.method,
                    createdAt: createdAtStr,
                };
            });
            setTransactions(txns);
        }, (err) => {
            console.error('Transaction listener error:', err);
        });

        return () => {
            unsubWallet();
            unsubTx();
        };
    }, [uid]);

    return { wallet, transactions, loading };
}

// ─── Cloud Function Callers ─────────────────────────────────────────────────

interface DepositPayload {
    amount: number;
    method: string;
    transactionId: string;
    senderNumber: string;
}

interface WithdrawalPayload {
    amount: number;
    method: string;
    receiverNumber: string;
}

interface CloudFnResult {
    success: boolean;
    message: string;
    requestId?: string;
    withdrawalId?: string;
    depositId?: string;
}

export async function callSubmitDeposit(data: DepositPayload): Promise<CloudFnResult> {
    const fn = httpsCallable<DepositPayload, CloudFnResult>(functions, 'submitDepositRequest');
    const result = await fn(data);
    return result.data;
}

export async function callSubmitWithdrawal(data: WithdrawalPayload): Promise<CloudFnResult> {
    const fn = httpsCallable<WithdrawalPayload, CloudFnResult>(functions, 'submitWithdrawalRequest');
    const result = await fn(data);
    return result.data;
}

// ─── Wallet PIN Functions ────────────────────────────────────────────────────

/** Set (or reset) the user's 6-digit Wallet PIN */
export async function callSetWalletPin(pin: string, currentPin?: string): Promise<CloudFnResult> {
    const fn = httpsCallable<{ pin: string; currentPin?: string }, CloudFnResult>(functions, 'setWalletPin');
    const result = await fn({ pin, currentPin });
    return result.data;
}

/** Verify the user's 6-digit Wallet PIN */
export async function callVerifyWalletPin(pin: string): Promise<CloudFnResult> {
    const fn = httpsCallable<{ pin: string }, CloudFnResult>(functions, 'verifyWalletPin');
    const result = await fn({ pin });
    return result.data;
}

/** Check if user has a PIN set up (reads from Firestore) */
export async function checkHasPinSetup(uid: string): Promise<boolean> {
    const { getDoc } = await import('firebase/firestore');
    const walletDoc = await getDoc(doc(db, `users/${uid}/private/wallet`));
    if (!walletDoc.exists()) return false;
    const data = walletDoc.data();
    return !!(data?.pinHash && data?.pinSalt);
}

// ─── Payment Method Config (Firestore Direct Read) ────────────────────────

export interface PaymentMethodNumber {
    id: string;
    number: string;
    type: 'personal' | 'merchant' | 'agent';
    active: boolean;
}

export interface PaymentMethodInfo {
    numbers: PaymentMethodNumber[];
    // Legacy fields kept for backward compat
    number?: string;
    type?: 'merchant' | 'personal';
    active?: boolean;
}

export interface PaymentMethodConfig {
    bkash?: PaymentMethodInfo;
    nagad?: PaymentMethodInfo;
    rocket?: PaymentMethodInfo;
}



/**
 * Real-time listener for payment method config from /config/paymentMethods.
 * Reads directly from Firestore — no Cloud Function involved.
 * Handles both new numbers[] array structure and legacy single-number structure.
 */
export function usePaymentMethodConfig() {
    const [data, setData] = useState<PaymentMethodConfig | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {

        const configRef = doc(db, 'config/paymentMethods');
        const unsub = onSnapshot(configRef, (snap) => {
            if (snap.exists()) {
                const raw = snap.data();
                const methods: ('bkash' | 'nagad' | 'rocket')[] = ['bkash', 'nagad', 'rocket'];
                const result: PaymentMethodConfig = {};

                for (const m of methods) {
                    const methodData = raw[m];
                    if (!methodData) continue;

                    if (methodData.numbers && Array.isArray(methodData.numbers)) {
                        // New multi-number structure
                        result[m] = {
                            numbers: methodData.numbers.map((n: any) => ({
                                id: n.id || '',
                                number: n.number || '',
                                type: n.type || 'personal',
                                active: n.active ?? true,
                            })),
                        };
                    } else if (methodData.number) {
                        // Legacy single-number: convert to numbers array
                        result[m] = {
                            numbers: [{
                                id: `legacy-${m}`,
                                number: methodData.number,
                                type: methodData.type || 'personal',
                                active: methodData.active ?? true,
                            }],
                            // Preserve legacy fields
                            number: methodData.number,
                            type: methodData.type,
                            active: methodData.active,
                        };
                    }
                }
                setData(result);
            } else {
                setData({}); // Document doesn't exist yet — no methods configured
            }
            setLoading(false);
        }, (err) => {
            console.error('Payment method config listener error:', err);
            setData({});
            setLoading(false);
        });

        return () => unsub();
    }, []);

    return { data, loading };
}

